import {useMemo} from 'react';

import {ConditionType} from 'common/constants/survey';
import {ConditionGroup, SurveySettings, SurveyValues} from 'common/models/survey';
import {isEmpty, isNil} from 'lodash';
import {Control, useWatch} from 'react-hook-form';
import isSeries from 'ui/utils/isSeries';

interface MatchConditionsParams {
    conditions?: ConditionGroup[];
    seriesName?: string;
    index?: number;
    question?: string;
}

export interface MatchConditions {
    (params: MatchConditionsParams): boolean;
}

const getConditionQuestions = (conditions: ConditionGroup[], result: Set<string>) => {
    conditions.forEach((conditionGroup) => {
        conditionGroup.items.forEach((condition) => {
            if (condition.type === ConditionType.Question && condition.question) {
                result.add(condition.question);
            }
        });
    });
};

const useDataWatch = (control: Control, survey?: SurveySettings): [SurveyValues, string[]] => {
    const watchItems = useMemo(() => {
        if (isEmpty(survey)) {
            return [];
        }

        const result = new Set<string>();

        if (survey?.conditions) {
            getConditionQuestions(survey.conditions, result);
        }

        survey?.pages.forEach(({items}) => {
            if (items) {
                items.forEach((item) => {
                    if (isNil(item)) {
                        return;
                    }
                    if (isSeries(item)) {
                        let hasInnerConditions = false;
                        item.items.forEach((question) => {
                            if (question.conditions) {
                                hasInnerConditions = true;
                                getConditionQuestions(question.conditions, result);
                            }
                        });
                        if (hasInnerConditions) {
                            result.add(item.id);
                        }
                    }
                    if (item.conditions) {
                        getConditionQuestions(item.conditions, result);
                    }
                });
            }
        });

        return Array.from(result);
    }, [survey]);

    const watchValues = useWatch({
        control,
        name: watchItems as never[],
    });

    const data = useMemo(
        () =>
            watchItems.reduce<SurveyValues>((result, item, index) => {
                result[item] = watchValues[index];
                return result;
            }, {}),
        [watchItems, watchValues],
    );

    return [data, watchItems];
};

export default useDataWatch;
